import React, { useState } from 'react';

import { Helmet } from 'react-helmet';

import { Container, Divider, Grid } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import {
  BlacklistAction,
  BulkSwitchGHAPropertiesAction,
  GHAPropertyType,
  bulkBlacklistProperties,
  bulkSwitchGHAProperties,
  bulkUpdateGHAPropertyType,
} from '~/services/ConnectionGoogleService';

import GHABulkOperationDialog from './GHABulkOperationDialog';
import GHABulkOperationsMenu from './GHABulkOperationsMenu';
import GHAPropertySearch from './GHAPropertySearch';

type DialogType = 'switch-properties' | 'update-property-type' | 'blacklist-properties' | null;

function GHAPropertyManagementContainer() {
  const [openDialog, setOpenDialog] = useState<DialogType>(null);

  const handleSwitchProperties = async (file: File, action: string) => {
    await bulkSwitchGHAProperties({ file, action: action as BulkSwitchGHAPropertiesAction });
  };

  const handleUpdatePropertyType = async (file: File, propertyType: string) => {
    await bulkUpdateGHAPropertyType(file, propertyType as GHAPropertyType);
  };

  const handleBlacklistProperties = async (file: File, action: string) => {
    await bulkBlacklistProperties({ file, action: action as BlacklistAction });
  };

  return (
    <Container maxWidth={false}>
      <Helmet>
        <title>GHA Property Management</title>
      </Helmet>
      <PageSubheader title="GHA Property Management" />
      <Grid container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid item>
          <GHABulkOperationsMenu
            onSwitchProperties={() => setOpenDialog('switch-properties')}
            onUpdatePropertyType={() => setOpenDialog('update-property-type')}
            onBlacklistProperties={() => setOpenDialog('blacklist-properties')}
          />
        </Grid>
      </Grid>

      {openDialog === 'switch-properties' && (
        <GHABulkOperationDialog
          open
          onClose={() => setOpenDialog(null)}
          title="Bulk Switch GHA Properties"
          radioLabel="Action"
          radioOptions={[
            { value: BulkSwitchGHAPropertiesAction.ENABLE, label: 'Enable on GHA' },
            { value: BulkSwitchGHAPropertiesAction.DISABLE, label: 'Disable on GHA' },
          ]}
          defaultRadioValue={BulkSwitchGHAPropertiesAction.ENABLE}
          submitButtonLabel="Submit"
          successMessage="Bulk operation submitted successfully."
          onSubmit={handleSwitchProperties}
        />
      )}

      {openDialog === 'update-property-type' && (
        <GHABulkOperationDialog
          open
          onClose={() => setOpenDialog(null)}
          title="Bulk Update GHA Property Type"
          radioLabel="GHA Property Type"
          radioOptions={[
            { value: GHAPropertyType.HOTEL, label: 'Hotel' },
            { value: GHAPropertyType.VACATION_RENTAL, label: 'Vacation Rental' },
          ]}
          defaultRadioValue={GHAPropertyType.HOTEL}
          submitButtonLabel="Update GHA Property Type"
          successMessage="Bulk GHA property type update is successful."
          onSubmit={handleUpdatePropertyType}
        />
      )}

      {openDialog === 'blacklist-properties' && (
        <GHABulkOperationDialog
          open
          onClose={() => setOpenDialog(null)}
          title="Bulk Blacklist Properties"
          radioLabel="Action"
          radioOptions={[
            { value: BlacklistAction.BLACKLIST, label: 'Blacklist' },
            { value: BlacklistAction.UNBLACKLIST, label: 'Unblacklist' },
          ]}
          defaultRadioValue={BlacklistAction.BLACKLIST}
          submitButtonLabel="Submit"
          successMessage="Bulk blacklist operation submitted successfully."
          onSubmit={handleBlacklistProperties}
        />
      )}

      <Divider sx={{ my: 2 }} />
      <GHAPropertySearch />
    </Container>
  );
}

export default GHAPropertyManagementContainer;
